import router from '@/router'
import { postPageLoaded } from '@/router/utils'
import store from '@/store/index'
import { aclHelper, aclPermsType, contractorPermsTypes } from '@/utils/aclHelper'
import EventBus from '@/utils/eventBus'
import { ENGINEER_DASHBOARD_ROUTE, tenderStatus } from '@/utils/locale'

import lodash from 'lodash'
import { getDashboard, hasCmsDashboardAccess, hasTmsDashboardAccess, hasWpDashboardAccess } from './dashboardUtils'

const nav = {
  lastDashboardTab: ENGINEER_DASHBOARD_ROUTE.TMS,

  setLastDashboardTab (val) {
    this.lastDashboardTab = val
  },

  toHome () {
    let home = this.lastDashboardTab
    const isHKlandUser = store.getters['user/isHKlandUser']
    if (router.currentRoute.fullPath.startsWith('/vendors')) {
      home = 'vendorHome'
    }
    if (router.currentRoute.fullPath.startsWith('/tenants')) {
      home = 'tenantDashboard'
    }
    if (isHKlandUser) {
      const foundDashboard = Object.entries(ENGINEER_DASHBOARD_ROUTE)
        .find(([_, val]) => val === home)
      if (!lodash.isUndefined(foundDashboard)) {
        home = getDashboard(foundDashboard[0], {
          hasCmsAccess: hasCmsDashboardAccess,
          hasTmsAccess: hasTmsDashboardAccess,
          hasWpAccess: hasWpDashboardAccess,
        })
      }
    }
    router.push({ name: home })
  },

  toLogin (redirectUrl) {
    const loginParams = { name: 'login' }
    const fullPath = router.currentRoute.fullPath
    if (fullPath.startsWith('/vendors') || fullPath.startsWith('/tenants')) {
      loginParams.name = 'publicLogin'
    }
    if (redirectUrl && redirectUrl !== '/404' && nav.isValidRedirect(fullPath, redirectUrl)) {
      loginParams.params = { redirect: redirectUrl }
    }
    router.push(loginParams, null, postPageLoaded)
  },

  toPage (name, params, query) {
    router.push({ name, params, query })
  },

  replacePage (name, params, query) {
    router.replace({ name, params, query })
  },

  isValidRedirect (path, redirectPath) {
    if (redirectPath.startsWith('/vendors')) {
      return path.startsWith('/vendors')
    } else if (redirectPath.startsWith('/tenants')) {
      return path.startsWith('/tenants')
    } else {
      return !path.startsWith('/vendors') && !path.startsWith('/tenants')
    }
  },
}

export default nav

const readAnyStatus = [tenderStatus.AWARDED, tenderStatus.REGRETTED, tenderStatus.CANCELLED]

const getTenderRouteName = (item) => {
  const editTenderRoute = item.version ? 'tenderAddendumDetails' : 'tenderDetails'

  const pageMap = {
    DRAFT: editTenderRoute,
    REJECTED: editTenderRoute,
    WAPPR: 'tenderDetailsView',
    APPR: 'tenderDetailsView',
    ISSUED: 'healthCheck',
    READY: 'healthCheck',
    REVIEW: 'analysisRecos',
    AWARDED: 'analysisRecos',
    REGRETTED: 'analysisRecos',
    CANCELLED: 'tenderDetailsView',
  }
  return pageMap[item.status]
}

const getTenderRouteNameExt = (item) => {
  const editTenderRoute = item.version ? 'tenderAddendumDetailsExt' : 'tenderDetailsExt'

  const pageMap = {
    DRAFT: editTenderRoute,
    REJECTED: editTenderRoute,
    WAPPR: 'tenderDetailsViewExt',
    APPR: 'tenderDetailsViewExt',
    ISSUED: 'healthCheckExt',
    READY: 'healthCheckExt',
    REVIEW: 'analysisRecosExt',
    AWARDED: 'analysisRecosExt',
    REGRETTED: 'analysisRecosExt',
    CANCELLED: 'tenderDetailsViewExt',
  }
  return pageMap[item.status]
}

/** Same branching as navToDetailPage; returns target route name or empty string when the user cannot open this tender */
export function resolveNavToTenderDetailRouteName (tender) {
  const userId = store.getters['user/currentUser'].id
  const createUpdateAccess = aclHelper.getAccess(aclPermsType.tenders.createUpdate)
  const representativeAccess = tender.isRepresentative &&
    aclHelper.getAccess(aclPermsType.tenders.review)
  const approverAccess = aclHelper.getAccess(aclPermsType.tenders.approve)
  const readAnyAccess = aclHelper.getAccess(aclPermsType.tenders.read)

  if (representativeAccess || (createUpdateAccess && (tender.managedBy === userId))) {
    return getTenderRouteName(tender) || ''
  }
  if (approverAccess && (tender.approvedBy === userId)) {
    return tender.status === tenderStatus.WAPPR ? 'tmView' : getTenderRouteName(tender) || ''
  }
  if (readAnyAccess || (readAnyStatus.indexOf(tender.status) >= 0 && (createUpdateAccess || representativeAccess))) {
    return getTenderRouteName(tender) || ''
  }
  return ''
}

export function navToDetailPage (tender, currentRouteName) {
  const target = resolveNavToTenderDetailRouteName(tender)
  if (!target) {
    EventBus.emit('modules_no_perm_modal_open')
    return
  }
  router.push({
    name: target,
    params: { id: tender.id, prev: currentRouteName },
  })
}

export function navToDetailPageExt (item, currentRouteName) {
  let target = ''
  const representativeAccess = item.isRepresentative &&
    aclHelper.getAccess(contractorPermsTypes.tenders.review)

  if (representativeAccess) {
    target = getTenderRouteNameExt(item)
  } else {
    EventBus.emit('modules_no_perm_modal_open')
    return
  }

  router.push({
    name: target,
    params: { id: item.id, prev: currentRouteName },
  })
}

export const navBackRouters = {
  projects: ['projects', 'allWorkPermits'],
}
const tenderRouteMapDetail = {
  healthCheck: [
    tenderStatus.ISSUED, tenderStatus.READY, tenderStatus.REVIEW, tenderStatus.REGRETTED, tenderStatus.AWARDED,
  ],
  analysisRecos: [tenderStatus.REVIEW, tenderStatus.REGRETTED, tenderStatus.AWARDED],
  tenderDetailsView: [tenderStatus.WAPPR, tenderStatus.APPR, tenderStatus.CANCELLED],
  tmView: [tenderStatus.WAPPR, tenderStatus.APPR],
  addendumSummary: [
    tenderStatus.ISSUED, tenderStatus.READY, tenderStatus.REVIEW, tenderStatus.REGRETTED, tenderStatus.AWARDED,
  ],
  tenderComparison: [tenderStatus.REVIEW, tenderStatus.REGRETTED, tenderStatus.AWARDED],
  queryStatus: [tenderStatus.REVIEW, tenderStatus.REGRETTED, tenderStatus.AWARDED],
  repReviewTender: [tenderStatus.REVIEW],
  repReviewAddendumTender: [tenderStatus.REVIEW],
}
const tenderRouteMapDetailExt = {
  healthCheckExt: [tenderStatus.ISSUED, tenderStatus.READY,
    tenderStatus.REVIEW, tenderStatus.REGRETTED, tenderStatus.AWARDED],
  analysisRecosExt: [tenderStatus.REVIEW, tenderStatus.REGRETTED, tenderStatus.AWARDED],
  tenderDetailsViewExt: [tenderStatus.WAPPR, tenderStatus.APPR, tenderStatus.CANCELLED],
  addendumSummaryExt: [tenderStatus.ISSUED, tenderStatus.READY,
    tenderStatus.REVIEW, tenderStatus.REGRETTED, tenderStatus.AWARDED],
  tenderComparisonExt: [tenderStatus.REVIEW, tenderStatus.REGRETTED, tenderStatus.AWARDED],
  queryStatusExt: [tenderStatus.REVIEW, tenderStatus.REGRETTED, tenderStatus.AWARDED],
}

export const validTenderRoute = (general, targetRouteName) => {
  const routeName = targetRouteName || router.currentRoute.name
  const status = general.status
  if (status) {
    if (status === tenderStatus.DRAFT) {
      return general.version > 0
        ? ['tenderAddendumDetails', 'tenderAddendumDetailsView', 'tenderDetailsView'].includes(routeName)
        : ['tenderDetails', 'tenderDetailsView', 'repEditTender'].includes(routeName)
    } else if (status === tenderStatus.REJECTED) {
      return general.version > 0
        ? ['tenderAddendumDetails', 'tenderAddendumDetailsView', 'tmView'].includes(routeName)
        : ['tenderDetails', 'tenderDetailsView', 'tmView', 'repEditTender'].includes(routeName)
    }
    return (deep(tenderRouteMapDetail, routeName) || []).includes(status)
  }
  return true
}

export const validTenderRouteExt = (general, targetRouteName) => {
  const routeName = targetRouteName || router.currentRoute.name
  const status = general.status
  if (status) {
    if (status === tenderStatus.DRAFT) {
      return general.version > 0
        ? ['tenderAddendumDetailsExt', 'tenderAddendumDetailsViewExt', 'tenderDetailsViewExt'].includes(routeName)
        : ['tenderDetailsExt', 'tenderDetailsViewExt'].includes(routeName)
    } else if (status === tenderStatus.REJECTED) {
      return general.version > 0
        ? ['tenderAddendumDetailsExt', 'tenderAddendumDetailsViewExt'].includes(routeName)
        : ['tenderDetailsExt', 'tenderDetailsViewExt'].includes(routeName)
    }
    return (deep(tenderRouteMapDetailExt, routeName) || []).includes(status)
  }
  return true
}

export function projectNavToDetailPage (id, isContractor, currentRouteName) {
  let name = isContractor ? 'vendorProjectDetail' : 'projectDetail'
  router.push({
    name,
    params: { id: id, prev: currentRouteName },
  })
}

export function goBack () {
  window.history.length > 2 ? router.go(-1) : router.push('/')
}

export function navToFromCompanyPageWithEdit (query) {
  if (query.from) {
    nav.toPage(query.from, { id: query.companyId }, { mode: 'edit' })
  } else {
    throw new Error('The from parameter of URL query is missing. Can not navigate to the source page.')
  }
}
